import { Alert, AlertDescription, AlertIcon, Box, Button, CloseButton, Flex } from '@chakra-ui/react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom'

import { ActionButton } from '../../../components/ActionButton'
import { EmptyState } from '../../../components/EmptyState'
import { FormStepper } from '../../../components/FormStepper'
import { FormSuccess } from '../../../components/FormSuccess'
import { LoadingBlock } from '../../../components/Loading'
import PublicFormLayout from '../../../components/publicFormLayout/PublicFormLayout'
import { useAppToast } from '../../../helpers'
import { withoutFieldErrors } from '../../../helpers/tabErrorUtils'
import { AdmissionService } from '../../../services/Admission/AdmissionService'
import {
  IAdmissionDocumentRaw,
  IAdmissionDraftRaw,
  IAdmissionOptionsRaw,
  IAdmissionSaveResponseRaw,
  TAdmissionSaveMode,
  TAdmissionStep
} from '../../../services/Admission/types'
import {
  EMPTY_FORM,
  formFromDraft,
  IAdmissionForm,
  stepFormData,
  stepOfError,
  TFormErrors,
  visibleSteps
} from './admissionForm'
import BenefitsStep from './steps/BenefitsStep'
import ContactsStep from './steps/ContactsStep'
import DocumentsStep from './steps/DocumentsStep'
import PersonalStep from './steps/PersonalStep'
import ProfessionalStep from './steps/ProfessionalStep'
import TransportStep from './steps/TransportStep'

const TITLE = 'Admissão de Colaborador'

type TLoadState = 'loading' | 'ready' | 'failed' | 'notFound'

/** What the API answered when the request was refused field by field (400, or 422 as a fallback). */
const validationErrors = (response: unknown): TFormErrors | null => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const error = response as any
  const status = error?.response?.status
  return (status === 400 || status === 422) && error.response.data?.errors ? error.response.data.errors : null
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isNotFound = (response: unknown) => (response as any)?.response?.status === 404

/**
 * The public admission form (ABRA-320): the hired person fills it in steps and the draft is
 * saved on the server at every step. "Salvar e continuar depois" e-mails a resume link
 * (`/admissao/continuar/:token`); sending makes the person a pending collaborator.
 */
export default function AdmissionFormPage() {
  const service = useMemo(() => new AdmissionService(), [])
  const toast = useAppToast()
  const navigate = useNavigate()
  const routeToken = useParams().token ?? null

  const [loadState, setLoadState] = useState<TLoadState>('loading')
  const [options, setOptions] = useState<IAdmissionOptionsRaw | null>(null)
  const [token, setToken] = useState<string | null>(routeToken)
  const [form, setForm] = useState<IAdmissionForm>(EMPTY_FORM)
  const [photo, setPhoto] = useState<File | null>(null)
  const [photoUrl, setPhotoUrl] = useState<string | null>(null)
  const [documents, setDocuments] = useState<IAdmissionDocumentRaw[]>([])
  const [vacancyClosed, setVacancyClosed] = useState(false)
  const [usernames, setUsernames] = useState<string[] | null>(null)
  const [step, setStep] = useState<TAdmissionStep>(1)
  const [errors, setErrors] = useState<TFormErrors>({})
  const [saving, setSaving] = useState<TAdmissionSaveMode | 'submit' | null>(null)
  const [savedNotice, setSavedNotice] = useState<{ email: string; sent: boolean } | null>(null)
  const [submitted, setSubmitted] = useState(false)
  const topRef = useRef<HTMLDivElement>(null)

  const steps = visibleSteps(form)
  const stepIndex = Math.max(
    0,
    steps.findIndex((item) => item.step === step)
  )
  const isLastStep = stepIndex === steps.length - 1
  const vacancy = options?.vacancies.find((item) => item.id === form.job_requisition_id)

  /** Drops the errors of the given fields (and of their items, `emergency_contacts.0.name`) once they are edited. */
  const clearErrors = useCallback((fields: string[]) => setErrors((current) => withoutFieldErrors(current, fields)), [])

  const update = useCallback(
    (patch: Partial<IAdmissionForm>) => {
      setForm((current) => ({ ...current, ...patch }))
      clearErrors(Object.keys(patch))
    },
    [clearErrors]
  )

  /** What the server keeps on its side: the photo, the documents and whether the vacancy closed. */
  const applyDraft = (draft: IAdmissionDraftRaw) => {
    setPhotoUrl(draft.data.photo_url)
    setDocuments(draft.documents)
    setVacancyClosed(draft.vacancy_closed)
  }

  const load = useCallback(() => {
    setLoadState('loading')

    Promise.all([service.options(), routeToken ? service.get(routeToken) : Promise.resolve(null)])
      .then(([loadedOptions, draft]) => {
        setOptions(loadedOptions)

        if (draft) {
          const restored = formFromDraft(draft.data)
          setForm(restored)
          applyDraft(draft)
          // Back where the person stopped: the step after the last one saved complete.
          const next = visibleSteps(restored).find((item) => item.step > draft.step)
          setStep(draft.vacancy_closed ? 2 : (next?.step ?? 6))
        }

        setLoadState('ready')
      })
      .catch((response) => setLoadState(isNotFound(response) ? 'notFound' : 'failed'))
    // The draft is read once: later saves answer with what changed on the server.
  }, [service])

  useEffect(load, [load])

  // The usernames come from the saved name, so they are read again on every visit to step 2.
  useEffect(() => {
    if (step !== 2 || !token) return

    setUsernames(null)
    service
      .usernames(token)
      .then(setUsernames)
      .catch((response) => {
        setUsernames([])
        toast.error('Erro ao carregar os nomes de usuário', response)
      })
  }, [step, token, service, toast])

  const goTo = (next: TAdmissionStep) => {
    setStep(next)
    setErrors({})
    topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const refreshVacancies = () =>
    service
      .options()
      .then(setOptions)
      .catch(() => undefined)

  const handleFailure = (response: unknown, title: string) => {
    if (isNotFound(response)) {
      setLoadState('notFound')
      return
    }

    const fieldErrors = validationErrors(response)
    // An empty `errors` has no step to go to: the step stays and the toast explains.
    if (!fieldErrors || Object.keys(fieldErrors).length === 0) {
      toast.error(title, response)
      return
    }

    // The vacancy closed meanwhile: the list is read again so the person picks one still open.
    if (fieldErrors.job_requisition_id) {
      update({ job_requisition_id: null })
      refreshVacancies()
    }

    const firstStep = Math.min(...Object.keys(fieldErrors).map(stepOfError)) as TAdmissionStep
    if (firstStep !== step) setStep(firstStep)
    setErrors(fieldErrors)
    toast.invalidForm()
  }

  /** Saves the current step: complete to move on, or as it is to continue later. */
  const save = async (mode: TAdmissionSaveMode): Promise<IAdmissionSaveResponseRaw> => {
    const data = stepFormData(step, form, step === 1 ? photo : null)
    data.append('mode', mode)
    if (mode === 'partial') data.append('send_link', '1')

    if (token) return service.save(token, data)

    const created = await service.create(data)
    setToken(created.token)
    // The resume link becomes the page's address, so a reload does not lose the draft.
    navigate(`/admissao/continuar/${created.token}`, { replace: true })
    return created
  }

  const afterSave = (response: IAdmissionSaveResponseRaw) => {
    applyDraft(response.draft)
    if (step === 1) setPhoto(null)
    setErrors({})
  }

  const advance = async () => {
    setSaving('step')
    setSavedNotice(null)
    try {
      afterSave(await save('step'))
      goTo(steps[stepIndex + 1].step)
    } catch (response) {
      handleFailure(response, 'Não foi possível salvar esta etapa')
    } finally {
      setSaving(null)
    }
  }

  const saveForLater = async () => {
    setSaving('partial')
    try {
      const response = await save('partial')
      afterSave(response)
      setSavedNotice({ email: form.email.trim(), sent: response.link_sent === true })
    } catch (response) {
      handleFailure(response, 'Não foi possível salvar o rascunho')
    } finally {
      setSaving(null)
    }
  }

  const submit = async () => {
    if (!token) return

    setSaving('submit')
    try {
      await service.submit(token)
      setSubmitted(true)
    } catch (response) {
      handleFailure(response, 'Não foi possível enviar a admissão')
    } finally {
      setSaving(null)
    }
  }

  if (submitted) {
    return (
      <PublicFormLayout title={TITLE}>
        <FormSuccess
          title="Admissão enviada!"
          description="Recebemos os seus dados e documentos. O RH vai conferir tudo e entrar em contato com os próximos passos."
          notes={['Seja bem-vindo(a) à ABRA! 💜']}
        />
      </PublicFormLayout>
    )
  }

  if (loadState !== 'ready' || !options) {
    return (
      <PublicFormLayout title={TITLE}>
        {loadState === 'loading' && <LoadingBlock entity="formulário" />}
        {loadState === 'failed' && (
          <EmptyState
            tone="error"
            icon="lni lni-warning"
            title="Não foi possível carregar o formulário"
            action={<ActionButton action="refresh" label="Tentar novamente" showLabel onClick={load} />}
          />
        )}
        {loadState === 'notFound' && (
          <EmptyState
            icon="lni lni-link"
            title="Link inválido ou expirado"
            description="Esta admissão já foi enviada ou o rascunho foi apagado depois de 7 dias sem alterações."
            action={
              <Button as={RouterLink} to="/admissao" colorScheme="magenta" reloadDocument>
                Começar uma nova admissão
              </Button>
            }
          />
        )}
      </PublicFormLayout>
    )
  }

  if (!token && options.vacancies.length === 0) {
    return (
      <PublicFormLayout title={TITLE}>
        <EmptyState
          icon="lni lni-briefcase"
          title="Nenhuma vaga aberta para admissão"
          description="No momento não há vagas abertas. Fale com o RH da ABRA."
        />
      </PublicFormLayout>
    )
  }

  const isDisabled = saving !== null
  const stepProps = { form, update, errors, isDisabled }

  return (
    <PublicFormLayout title={TITLE}>
      <Box ref={topRef} scrollMarginTop="16px">
        <FormStepper
          steps={steps}
          activeIndex={stepIndex}
          onStepClick={isDisabled ? undefined : (index) => goTo(steps[index].step)}
        />
      </Box>

      <form
        noValidate
        onSubmit={(event) => {
          event.preventDefault()
          if (isLastStep) submit()
          else advance()
        }}
      >
        {step === 1 && (
          <PersonalStep
            {...stepProps}
            photo={photo}
            photoUrl={photoUrl}
            onPhotoChange={(file) => {
              setPhoto(file)
              clearErrors(['photo'])
            }}
          />
        )}
        {step === 2 && (
          <ProfessionalStep
            {...stepProps}
            vacancies={options.vacancies}
            usernames={usernames}
            vacancyClosed={vacancyClosed && !form.job_requisition_id}
            hasDocuments={documents.length > 0}
          />
        )}
        {step === 3 && <ContactsStep {...stepProps} />}
        {step === 4 && <BenefitsStep {...stepProps} term={options.benefit_term} />}
        {step === 5 && <TransportStep {...stepProps} />}
        {step === 6 && token && (
          <DocumentsStep
            token={token}
            vacancy={vacancy}
            documents={documents}
            onUploaded={(document) => {
              setDocuments((current) => [
                ...current.filter((item) => item.job_position_document_id !== document.job_position_document_id),
                document
              ])
              clearErrors([`documents.${document.job_position_document_id}`])
            }}
            onRemoved={(documentId) =>
              setDocuments((current) => current.filter((item) => item.job_position_document_id !== documentId))
            }
            errors={errors}
            isDisabled={isDisabled}
          />
        )}

        {savedNotice && (
          <Alert status={savedNotice.sent ? 'success' : 'warning'} borderRadius="md" mt={8} alignItems="flex-start">
            <AlertIcon />
            <AlertDescription flex={1}>
              {savedNotice.sent
                ? `Rascunho salvo. Enviamos para ${savedNotice.email} um link para você continuar de onde parou. Ele fica guardado por 7 dias sem alterações.`
                : 'Rascunho salvo, mas não conseguimos enviar o e-mail agora. Guarde o endereço desta página para continuar depois: ele fica guardado por 7 dias sem alterações.'}
            </AlertDescription>
            <CloseButton size="sm" onClick={() => setSavedNotice(null)} aria-label="Fechar aviso" />
          </Alert>
        )}

        <Flex mt={8} gap={3} direction={{ base: 'column-reverse', md: 'row' }} align={{ md: 'center' }}>
          {stepIndex > 0 && (
            <Button variant="ghost" onClick={() => goTo(steps[stepIndex - 1].step)} isDisabled={isDisabled}>
              Voltar
            </Button>
          )}
          <Box flex={1} display={{ base: 'none', md: 'block' }} />
          {form.email.trim() && (
            <Button
              variant="outline"
              colorScheme="magenta"
              onClick={saveForLater}
              isLoading={saving === 'partial'}
              isDisabled={isDisabled && saving !== 'partial'}
            >
              Salvar e continuar depois
            </Button>
          )}
          <Button
            type="submit"
            colorScheme="magenta"
            isLoading={saving === 'step' || saving === 'submit'}
            isDisabled={isDisabled && saving === 'partial'}
          >
            {isLastStep ? 'Enviar admissão' : 'Avançar'}
          </Button>
        </Flex>
      </form>
    </PublicFormLayout>
  )
}
